import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import {
  DataService,
  FormInputComponent,
  LanguageCode,
  SharedModule,
  StringCustomFieldConfig,
} from "@vendure/admin-ui/core";
import { Apollo, gql } from "apollo-angular";
import { Observable, of, Subject } from "rxjs";
import {
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { CountryStateService } from "./country-state.service";

@Component({
  selector: "app-country-selector",
  imports: [SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  template: `
    <div style="position:relative">
      <ng-select
        [items]="transformedOptions"
        [bindValue]="'value'"
        [bindLabel]="'displayLabel'"
        placeholder="Select a country"
        [formControl]="formControl"
        (change)="handleChange($event)"
        [dropdownPosition]="'top'"
        class="form-control"
      >
      </ng-select>
    </div>
  `,
})
export class CountrySelectorComponent
  implements OnInit, OnDestroy, FormInputComponent<StringCustomFieldConfig>
{
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;
  transformedOptions: any[] = [];
  private languageCode$: Observable<LanguageCode>;
  private destroy$ = new Subject<void>();
  private isInitializing = true;

  private query = gql`
    query GetCountryByCode($code: String!) {
      getCountryByCode(code: $code) {
        id
        name
        customFields {
          states
        }
      }
    }
  `;

  private getAllCountries = gql`
    query GetAllCountries {
      countries {
        items {
          id
          name
          code
        }
      }
    }
  `;

  constructor(
    private apollo: Apollo,
    private countryStateService: CountryStateService,
    private cdr: ChangeDetectorRef,
    private dataService: DataService,
    private route: ActivatedRoute,
  ) {
    console.log(
      "🏗️ CountrySelector Constructor called at",
      new Date().toISOString(),
    );
    console.log("🔍 Component config:", this.config);
    console.log("🔍 Form control initial value:", this.formControl?.value);
    console.log("🔍 Readonly mode:", this.readonly);
  }

  ngOnInit() {
    console.log("🚀 ngOnInit started at", new Date().toISOString());
    console.log("📝 Initial form control value:", this.formControl.value);

    // Setup language stream first
    console.log("🌐 Setting up language stream...");
    this.setupLanguageStream();

    // Subscribe to language changes (this will trigger fetchAllCountries)
    console.log("👂 Subscribing to language changes...");
    this.subscribeToLanguageChanges();

    // Handle initial value if exists
    if (this.formControl.value) {
      console.log(
        "🎯 Initial value detected, calling fetchCountryDetails with:",
        this.formControl.value,
      );
      this.fetchCountryDetails(this.formControl.value);
    } else {
      console.log("❌ No initial value in form control");
    }

    // Subscribe to form control value changes
    console.log("👂 Setting up form control value changes subscription...");
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        console.log(
          "📊 Form control value changed to:",
          value,
          "at",
          new Date().toISOString(),
        );
        if (!value) {
          console.log("❌ Value is empty, skipping fetchCountryDetails");
          return;
        }
        console.log(
          "🎯 Calling fetchCountryDetails from valueChanges with:",
          value,
        );
        this.fetchCountryDetails(value);
      });

    console.log("✅ ngOnInit completed at", new Date().toISOString());
  }

  ngOnDestroy() {
    console.log("💀 ngOnDestroy called");
    this.destroy$.next();
    this.destroy$.complete();
    this.countryStateService.emitCountryId(null);
  }

  private setupLanguageStream() {
    console.log("🌐 setupLanguageStream called");
    this.languageCode$ = this.route.paramMap.pipe(
      map((paramMap) => {
        const lang = paramMap.get("lang");
        console.log("🗺️ Route param 'lang':", lang);
        return lang;
      }),
      switchMap((lang) => {
        if (lang) {
          console.log("🌍 Using language from route:", lang);
          return of(lang as LanguageCode);
        } else {
          console.log("🌍 No language in route, getting from UI state...");
          return this.dataService.client.uiState().mapSingle((data) => {
            console.log("🌍 UI state language:", data.uiState.contentLanguage);
            return data.uiState.contentLanguage;
          });
        }
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  private subscribeToLanguageChanges() {
    console.log("👂 subscribeToLanguageChanges called");
    this.languageCode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((languageCode) => {
        console.log(
          "🌐 Language changed to:",
          languageCode,
          "at",
          new Date().toISOString(),
        );
        console.log("🏴 Calling fetchAllCountries due to language change...");
        this.fetchAllCountries();
      });
  }

  handleChange(selectedValue: {
    id: string;
    value: string;
    displayLabel: string;
  }) {
    console.log(
      "🎛️ handleChange called with:",
      selectedValue,
      "at",
      new Date().toISOString(),
    );
    console.log("🔄 isInitializing:", this.isInitializing);

    if (selectedValue?.id) {
      console.log("✅ selectedValue has ID:", selectedValue.id);
      console.log("📤 Emitting country ID to service:", selectedValue.id);
      this.countryStateService.emitCountryId(selectedValue.id);
    } else {
      console.log("❌ selectedValue has no ID, emitting null");
      this.countryStateService.emitCountryId(null);
    }
  }

  private fetchAllCountries() {
    console.log("🌍 fetchAllCountries called at", new Date().toISOString());

    this.apollo
      .query({
        query: this.getAllCountries,
        fetchPolicy: "network-only",
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          console.log(
            "✅ fetchAllCountries SUCCESS at",
            new Date().toISOString(),
          );
          console.log("📊 Countries data:", result.data);

          const countries = result.data.countries.items;
          console.log("🏴 Number of countries:", countries.length);

          this.transformedOptions = countries.map((country: any) => ({
            id: country.id,
            value: country.code,
            displayLabel: country.name,
          }));

          console.log("🔄 Transformed options:", this.transformedOptions);

          // Check if current form value exists in the options
          const currentValue = this.formControl.value;
          if (currentValue) {
            const foundOption = this.transformedOptions.find(
              (opt) => opt.value === currentValue,
            );
            console.log(
              "🔍 Found option for current value '" + currentValue + "':",
              foundOption,
            );
          }

          this.cdr.detectChanges();
          console.log("🔄 Change detection triggered after fetchAllCountries");

          // Mark initialization as complete after a short delay
          setTimeout(() => {
            this.isInitializing = false;
            console.log("✅ Initialization marked as complete");
          }, 100);
        },
        error: (error) => {
          console.error("❌ fetchAllCountries ERROR:", error);
        },
      });
  }

  private fetchCountryDetails(code: string) {
    console.log(
      "🎯 fetchCountryDetails called with code:",
      code,
      "at",
      new Date().toISOString(),
    );

    this.apollo
      .query({
        query: this.query,
        variables: { code },
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          console.log(
            "✅ fetchCountryDetails SUCCESS at",
            new Date().toISOString(),
          );
          console.log("📊 Country details result:", result.data);

          const country = result.data.getCountryByCode;
          if (country) {
            const countryId = country.id;
            console.log("🆔 Country ID from fetchCountryDetails:", countryId);
            console.log("🏴 Country name:", country.name);
            console.log(
              "📤 Emitting country ID from fetchCountryDetails:",
              countryId,
            );
            this.countryStateService.emitCountryId(countryId);
          } else {
            console.log("❌ No country found for code:", code);
          }
        },
        error: (error) => {
          console.error(
            "❌ fetchCountryDetails ERROR for code '" + code + "':",
            error,
          );
        },
      });
  }
}
